import { Business as BusinessIcon } from "@mui/icons-material";
import { Avatar, Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { RenderStates, type RenderStatesProps } from "~ui/atoms/RenderStates/RenderStates";
import { EntityListSearchField } from "~ui/molecules/EntityListSearchField/EntityListSearchField";

const tNoSearchMatch = "No schools match your search.";

type GridItemSize = number | { lg?: number; md?: number; sm?: number; xl?: number; xs?: number };

export interface School {
  id: string;
  bubbleId: string;
  name: string;
  memberCount: number;
  requestCount: number;
  avatarUri?: string;
}

export type SchoolsTemplateBaseProps = Pick<RenderStatesProps<School[]>, "data" | "status">;

export type SchoolsTemplateProps = SchoolsTemplateBaseProps & {
  /** MUI Grid2 `size` for each school card. Default full width (12). */
  itemSize?: GridItemSize;
  /** When true, show a search field and filter schools by name. */
  showSearch?: boolean;
};

const schoolListContent = (school: School) => (
  <Button
    component={RouterLink}
    sx={{
      borderRadius: "4px",
      color: "inherit",
      justifyContent: "flex-start",
      padding: {
        md: "1rem",
        sm: "0.75rem",
        xs: "0.5rem",
      },
      textAlign: "left",
      textTransform: "none",
      width: "100%",
    }}
    to={`/admin?adminSelect=${school.bubbleId}`}
    variant="text"
  >
    <Grid alignItems="center" container direction="row" gap={{ md: "2.5rem", sm: "1.5rem", xs: "1rem" }}>
      <Grid container direction="row" size="grow">
        <Grid>
          <Avatar
            slotProps={{
              img: {
                "aria-label": `${school.name} school avatar`,
              },
            }}
            src={school.avatarUri}
            sx={{
              height: { md: "3.5rem", sm: "3rem", xs: "2.5rem" },
              width: { md: "3.5rem", sm: "3rem", xs: "2.5rem" },
            }}
          >
            <BusinessIcon />
          </Avatar>
        </Grid>
        <Grid marginLeft={{ md: "1em", sm: "0.75em", xs: "0.5em" }}>
          <Typography
            component="p"
            sx={{
              fontSize: { md: "1.25rem", sm: "1rem", xs: "0.9rem" },
            }}
            variant="h6"
          >
            {school.name}
          </Typography>
          <Typography
            sx={{
              fontSize: { md: "0.875rem", sm: "0.8125rem", xs: "0.75rem" },
            }}
            variant="body2"
          >
            {school.memberCount} members • {school.requestCount} pending requests
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  </Button>
);

export const SchoolsTemplate = ({
  data,
  itemSize: itemSizeProp = 12,
  showSearch = false,
  status,
}: SchoolsTemplateProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  return (
    <RenderStates
      data={data}
      status={status}
      empty={<Typography>No schools found</Typography>}
      error={<Typography color="error">Error loading schools</Typography>}
      pending={<CircularProgress />}
      success={(schools) => {
        const q = showSearch ? searchQuery.trim().toLowerCase() : "";
        const list = !q ? schools : schools.filter((s) => s.name.toLowerCase().includes(q));
        const noSearchResults = showSearch && q && list.length === 0;

        if (noSearchResults) {
          return (
            <Box>
              <EntityListSearchField
                id="schools-template-search"
                label="Search schools"
                value={searchQuery}
                onChange={({ target: { value } }) => {
                  setSearchQuery(value);
                }}
              />
              <Typography sx={{ mt: 2 }}>{tNoSearchMatch}</Typography>
            </Box>
          );
        }

        return (
          <Box>
            {showSearch && (
              <EntityListSearchField
                id="schools-template-search"
                label="Search schools"
                sx={{ mb: 2 }}
                value={searchQuery}
                onChange={({ target: { value } }) => {
                  setSearchQuery(value);
                }}
              />
            )}
            <Grid container spacing={2}>
              {list.map((school) => (
                <Grid key={school.id} size={itemSizeProp}>
                  {schoolListContent(school)}
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      }}
    />
  );
};
